import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';

interface Capabilities {
  demoData: boolean;
}

/** `before` is the reference corpus; `after` adds the corpus measured under the published version (phase 6). */
export type DemoPhase = 'before' | 'after';

interface SeedResult {
  phase: DemoPhase;
  eventCount: number;
  journeyCount: number;
  configurationVersion: number | null;
  guidedSteps: string[];
}

/** Demonstration affordances; the API offers them where DEMO_DATA_ENABLED allows (ADR 0013). */
export function useDemoData() {
  const queryClient = useQueryClient();
  const { data: capabilities } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => api<Capabilities>('/capabilities'),
    staleTime: Infinity,
  });

  const seed = useMutation({
    mutationFn: (phase: DemoPhase) =>
      api<SeedResult>('/administration/demo-data', { method: 'POST', body: JSON.stringify({ phase }) }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const reset = useMutation({
    mutationFn: (confirmation: string) =>
      api<{ deletedEvents: number }>('/administration/demo-reset', {
        method: 'POST',
        body: JSON.stringify({ confirmation }),
      }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return { available: capabilities?.demoData ?? false, seed, reset };
}
